import { CategoryChartQueryFn, CategoryChartQueryParam } from "../categoryChart";
import { getBaseValue, Unit } from "../processors";
import _, { result } from "lodash";
import { HorizontalTwoLineLabelKpiProviders } from "./categoryChartData.types";
import { getKpisForProvidersWithCategoryChart } from "../opendata";
import { VALUE_STATUS_NAMING } from "../../Components/misc";

export const fetchHorizontalTwoLineProviders = async (
    args: CategoryChartQueryParam
): Promise<HorizontalTwoLineLabelKpiProviders> => {
    const { categoryChart, providers, kpis } = args;

    if (!providers || providers?.length === 0 || !categoryChart || !kpis || kpis?.length === 0) return null;

    const { visHeader, visFooter, displayTitle } = categoryChart;

    const kpiReportResults = await getKpisForProvidersWithCategoryChart(kpis, providers, false, categoryChart);

    const kpiCodes = kpis.map(d => d?.[0]);
    const series = kpis.map(d => d?.[2]);
    const kpiCodeToSeriesName = kpiCodes.reduce((prev, cur, index) => {
        prev[cur] = series?.[index] ?? "unknown";
        return prev;
    }, {});

    const kpiCodeToKpiReportResults = _.groupBy(kpiReportResults, d => d.kpiCode);
    const orderedKpiReportResults = _.flattenDeep(
        kpiCodes.map(kpiCode => {
            return kpiCodeToKpiReportResults?.[kpiCode] ?? [];
        })
    );

    const providerTIdToOrderedResults = _.groupBy(orderedKpiReportResults, d => d?.provider?.tid);
    let unit: Unit = null;

    const latestYear = _.orderBy(_.uniq(orderedKpiReportResults?.map(d => d.year)), d => d, "desc")?.[0];

    const providerResults = providers.map(provider => {
        const results = providerTIdToOrderedResults?.[provider?.tid];
        // ?.filter(d => d.value != null);

        const visHeaderProcessed =
            visHeader?.value == null || visHeader?.value?.trim() === ""
                ? null
                : _.template(visHeader?.value ?? "")({
                      startYear: "",
                      endYear: latestYear
                  });

        const visFooterProcessed =
            visFooter?.value == null || visFooter?.value?.trim() === ""
                ? null
                : _.template(visFooter?.value ?? "")({
                      startYear: "",
                      endYear: latestYear
                  });

        const naRet = {
            figure: {
                header: null,
                value: VALUE_STATUS_NAMING.NA,
                decimals: 0,
                footer: null,
                unit: null
            },
            graph: {
                header:
                    visHeaderProcessed == null
                        ? null
                        : {
                              processed: visHeaderProcessed,
                              value: visHeaderProcessed,
                              format: visHeaderProcessed
                          },
                footer:
                    visFooterProcessed == null
                        ? null
                        : {
                              processed: visFooterProcessed,
                              value: visFooterProcessed,
                              format: visFooterProcessed
                          }
            }
        };

        if (!result?.length) {
            return naRet;
        }

        const yearDescReports = _.orderBy(results, d => d.year, "desc");

        // const latestYear = yearDescReports?.[0]?.year;
        const initialYear =
            yearDescReports?.[yearDescReports?.length - 1]?.year === latestYear
                ? null
                : yearDescReports?.[yearDescReports?.length - 1]?.year;
        if (latestYear == null) return naRet;

        const _visHeaderProcessed =
            visHeader?.value == null || visHeader?.value?.trim() === ""
                ? null
                : _.template(visHeader?.value ?? "")({
                      startYear: initialYear,
                      endYear: latestYear
                  });

        const _visFooterProcessed =
            visFooter?.value == null || visFooter?.value?.trim() === ""
                ? null
                : _.template(visFooter?.value ?? "")({
                      startYear: initialYear,
                      endYear: latestYear
                  });

        const graphHeader =
            _visHeaderProcessed == null
                ? null
                : {
                      processed: _visHeaderProcessed,
                      value: _visHeaderProcessed,
                      format: _visHeaderProcessed
                  };

        const graphFooter =
            _visFooterProcessed == null
                ? null
                : {
                      processed: _visFooterProcessed,
                      value: _visFooterProcessed,
                      format: _visFooterProcessed
                  };

        naRet.graph.header = graphHeader;
        naRet.graph.footer = graphFooter;

        const latestYearData = yearDescReports?.filter(d => d.year === latestYear);
        const baseLatestYearData = latestYearData?.map(d => {
            if (d?.value == null) return d;

            const { unit, value } = getBaseValue(d.value, d.unit);
            return {
                ...d,
                unit,
                value
            };
        });
        unit = _.uniq(baseLatestYearData.map(d => d.unit))?.[0];
        if (unit == null) return naRet;

        const nonNullResults = baseLatestYearData?.filter(d => d.value != null);
        const nullResults = baseLatestYearData?.filter(d => d.value == null);
        const sum = _.sum(nonNullResults?.map(d => d.value));

        const valueStatus =
            nullResults?.length === baseLatestYearData?.length ? baseLatestYearData?.[0]?.valueStatus : undefined;

        if (nullResults?.length === baseLatestYearData?.length)
            return {
                ...naRet,
                figure: {
                    ...naRet.figure,
                    value: valueStatus ?? VALUE_STATUS_NAMING.NA,
                    decimals: 0,
                    footer: null,
                    unit
                },
                graph: {
                    ...naRet.graph,
                    header: graphHeader,
                    footer: graphFooter
                }
            };

        if (sum === 0)
            return {
                ...naRet,

                graph: {
                    ...naRet.graph,
                    header: graphHeader,
                    footer: graphFooter
                },
                figure: {
                    ...naRet.figure,
                    value: 0,
                    decimals: 0,
                    footer: null,
                    unit
                }
            };

        return {
            figure: null,
            graph: {
                header: graphHeader,
                footer: graphFooter,
                data: baseLatestYearData.map(d => {
                    return {
                        value: d.value,
                        label: d.scheme,
                        seriesName: kpiCodeToSeriesName?.[d.kpiCode]
                    };
                })
            }
        };
    });

    return {
        title: displayTitle,
        elements: providerResults,
        waterProviders: providers,
        unit,
        year: null
    };
};
export const fetchHorizontalTwoLineComparisonChartData: CategoryChartQueryFn = async (
    args: CategoryChartQueryParam
): Promise<HorizontalTwoLineLabelKpiProviders> => {
    return fetchHorizontalTwoLineProviders(args);
};
